import type { AxiosRequestConfig, AxiosResponse } from "axios";

import type { TrackedArticleRankingSource } from "shared/stores/frontpages";
import type {
  CurrentUser,
  Preferences,
  UpdateUser,
} from "shared/stores/user/types";
import type { Nullable } from "shared/types";

import ApiClient from "./ApiClient";

interface ChangePasswordWithCodeParams {
  code: string;
  email_or_phone: string;
  password: string;
  reset_key?: never;
}

interface ChangePasswordWithResetKeyParams {
  code?: never;
  email_or_phone?: never;
  password: string;
  reset_key: string;
}

export type ChangePasswordParams =
  | ChangePasswordWithCodeParams
  | ChangePasswordWithResetKeyParams;

export interface CslUrlResponse {
  url: string;
}

export interface EmailRecipientList {
  created_at: string;
  id: number;
  recipient_emails: string[];
  recipients: string;
  user_id: number;
}

export interface EnableTwoFactorResponse {
  verify: Nullable<string>;
}

export interface NewsSummarySubscriptionResponse {
  email?: boolean;
  email_digests?: string[];
  enabled?: boolean;
  featured?: boolean;
  id?: number;
  mobile?: boolean;
  mobile_digests?: string[];
  recipients?: string[];
  regular?: boolean;
  user_id?: number;
}

export interface ContentTeaserFeatureAccessParams {
  additional_information: string;
  content_types: string[];
}

export interface FeatureAccessParams {
  additional_information: string;
  feature: string;
}

export interface InsightsPlusFeatureAccessParams extends FeatureAccessParams {
  frequency: string;
}

export type RequestFeatureAccessParams =
  | ContentTeaserFeatureAccessParams
  | FeatureAccessParams
  | InsightsPlusFeatureAccessParams;

export interface RequestOutreachChangeParams {
  contact_id?: Nullable<number>;
  email: string;
  message: string;
  name: string;
  region: number;
  request_type: "general" | "influencer" | "outlet";
}

export interface StockPriceData {
  change?: string;
  change_percent?: string;
  currency?: string;
  price?: string;
  volume?: string;
}

export interface StockPrice {
  data: StockPriceData;
  symbol: string;
}

export interface SuccessResponse {
  success: boolean;
}

export interface UpdateUserRequest extends UpdateUser {
  first_name?: string;
  last_name?: string;
  notes?: string;
  organisation_team_ids?: number[];
  primary_organisation_team?: number;
}

export interface UpdateUserParams {
  user: UpdateUserRequest;
}

export interface ValidateResetCodeParams {
  code: string;
  email_or_phone: string;
}

class UsersService extends ApiClient {
  constructor() {
    super("/users");
  }

  changePassword(
    params: ChangePasswordParams,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<void>> {
    return this.post<void>("/change_password", { ...options, params });
  }

  enableTwoFactor(
    carrier: string,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<EnableTwoFactorResponse>> {
    return this.post<EnableTwoFactorResponse>("/enable_two_factor", {
      ...options,
      params: { carrier },
    });
  }

  getCslUrl(
    userId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<CslUrlResponse>> {
    return this.get<CslUrlResponse>(`/${userId}/csl_url`, options);
  }

  getEmailRecipientLists(
    userId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<EmailRecipientList[]>> {
    return this.get<EmailRecipientList[]>(
      `/${userId}/email_recipient_lists`,
      options
    );
  }

  getNewsSummarySubscription(
    userId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<NewsSummarySubscriptionResponse>> {
    return this.get<NewsSummarySubscriptionResponse>(
      `/${userId}/news_summary_subscription`,
      options
    );
  }

  getStockPrices(
    userId: number,
    symbols: string[],
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<StockPrice[]>> {
    return this.get<StockPrice[]>(`/${userId}/stock_prices`, {
      ...options,
      params: { symbols },
    });
  }

  getTrackedArticleRankingSources(
    userId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<TrackedArticleRankingSource[]>> {
    return this.get<TrackedArticleRankingSource[]>(
      `/${userId}/tracked_article_ranking_sources`,
      options
    );
  }

  removeRecipient(
    userId: number,
    recipient: string,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<SuccessResponse>> {
    return this.put<SuccessResponse>(`/${userId}/remove_recipient`, {
      ...options,
      params: { recipient },
    });
  }

  requestFeatureAccess(
    userId: number,
    params: RequestFeatureAccessParams,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<void>> {
    return this.post<void>(`/${userId}/request_feature_access`, {
      ...options,
      params,
    });
  }

  requestOutreachChange(
    data: RequestOutreachChangeParams,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<SuccessResponse>> {
    return this.post<SuccessResponse>("/outreach_change_request", {
      ...options,
      data,
    });
  }

  resetPassword(
    userId: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<void>> {
    return this.post<void>(`/${userId}/reset_password`, options);
  }

  sendTwoFactorCode(
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<void>> {
    return this.post<void>("/send_two_factor_code", options);
  }

  updatePreferences<K extends keyof Preferences>(
    userId: number,
    attribute: K,
    value: Preferences[K],
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<CurrentUser>> {
    return this.put<CurrentUser>(`/${userId}/update_preferences`, {
      handleErrors: true,
      ...options,
      params: { attribute, value },
    });
  }

  updateTrackedArticleRankingSourcePosition(
    userId: number,
    sourceId: number,
    position: number,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<void>> {
    return this.put<void>(
      `/${userId}/tracked_article_ranking_sources/${sourceId}`,
      {
        ...options,
        params: { position },
      }
    );
  }

  updateUser(
    userId: number,
    params: UpdateUserParams,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<CurrentUser>> {
    return this.put<CurrentUser>(`/${userId}`, { ...options, params });
  }

  validateResetCode(
    params: ValidateResetCodeParams,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<{ confirmation: string }>> {
    return this.post<{ confirmation: string }>("/validate_reset_code", {
      ...options,
      params,
    });
  }

  validateTwoFactor(
    code: string,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<void>> {
    return this.post<void>("/validate_two_factor", {
      ...options,
      params: { code },
    });
  }
}

export default new UsersService();
